import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { LogIn, LogOut, Clock } from 'lucide-react';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function AttendancePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [todayRecord, setTodayRecord] = useState(null);
  const [todayAll, setTodayAll] = useState([]);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [month, year]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const [todayRes, historyRes] = await Promise.all([
          api.get('/attendance/today'),
          api.get(`/attendance?month=${month}&year=${year}`),
        ]);
        setTodayAll(todayRes.data);
        setHistory(historyRes.data);
      } else {
        const [todayRes, historyRes] = await Promise.all([
          api.get('/attendance/today'),
          api.get(`/attendance?month=${month}&year=${year}`),
        ]);
        setTodayRecord(todayRes.data);
        setHistory(historyRes.data);
      }
    } catch {
      toast.error('Failed to load attendance');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      await api.post('/attendance/check-in');
      toast.success('Checked in successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  };

  const handleCheckOut = async () => {
    try {
      await api.post('/attendance/check-out');
      toast.success('Checked out successfully');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-out failed');
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div data-testid="attendance-page">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">
          {isAdmin ? 'Attendance' : 'My Attendance'}
        </h2>
        <p className="text-sm text-muted-foreground font-body mt-1">Track daily attendance records</p>
      </div>

      {/* Employee check-in/out section */}
      {!isAdmin && (
        <div className="border border-border bg-card rounded-sm p-5 mb-6">
          <h3 className="font-heading font-semibold text-foreground mb-3">Today's Attendance</h3>
          {!todayRecord ? (
            <div className="flex items-center gap-4">
              <p className="text-sm text-muted-foreground">Not checked in yet</p>
              <Button onClick={handleCheckIn} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="attendance-check-in-btn">
                <LogIn className="w-4 h-4 mr-2" strokeWidth={1.5} /> Check In
              </Button>
            </div>
          ) : !todayRecord.check_out ? (
            <div className="flex items-center gap-4">
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Checked In at {formatTime(todayRecord.check_in)}</Badge>
              <Button onClick={handleCheckOut} variant="outline" className="rounded-sm" data-testid="attendance-check-out-btn">
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} /> Check Out
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <Badge className="bg-muted text-muted-foreground border-border">Day Complete</Badge>
              <span className="text-sm text-muted-foreground font-mono">{formatTime(todayRecord.check_in)} - {formatTime(todayRecord.check_out)}</span>
              <span className="text-sm text-muted-foreground font-mono">({todayRecord.hours_worked}h)</span>
            </div>
          )}
        </div>
      )}

      {/* Admin today overview */}
      {isAdmin && (
        <div className="border border-border bg-card rounded-sm p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-foreground">Today's Attendance</h3>
            <Badge variant="secondary" className="font-mono text-xs">{todayAll.length} present</Badge>
          </div>
          {todayAll.length === 0 ? (
            <p className="text-sm text-muted-foreground">No attendance records for today</p>
          ) : (
            <div className="border border-border rounded-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">ID</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Check In</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Check Out</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Hours</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {todayAll.map(rec => (
                    <TableRow key={rec.id}>
                      <TableCell className="font-medium">{rec.employee_name}</TableCell>
                      <TableCell className="font-mono text-xs">{rec.emp_id}</TableCell>
                      <TableCell className="font-mono text-xs">{formatTime(rec.check_in)}</TableCell>
                      <TableCell className="font-mono text-xs">{formatTime(rec.check_out)}</TableCell>
                      <TableCell className="font-mono text-xs">{rec.hours_worked || '-'}</TableCell>
                      <TableCell>
                        <Badge className={rec.check_out ? 'bg-muted text-muted-foreground border-border' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}>
                          {rec.check_out ? 'Complete' : 'Active'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Month filter + history */}
      <div className="border border-border bg-card rounded-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground">
            <Clock className="w-4 h-4 inline mr-2" strokeWidth={1.5} />
            Attendance History
          </h3>
          <div className="flex gap-2">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-32 rounded-sm" data-testid="attendance-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24 rounded-sm" data-testid="attendance-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Loading...</p>
        ) : history.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No records for this period</p>
        ) : (
          <div className="border border-border rounded-sm">
            <Table>
              <TableHeader>
                <TableRow>
                  {isAdmin && <TableHead className="text-xs uppercase tracking-widest">Employee</TableHead>}
                  <TableHead className="text-xs uppercase tracking-widest">Date</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Check In</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Check Out</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Hours</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(rec => (
                  <TableRow key={rec.id}>
                    {isAdmin && <TableCell className="font-medium">{rec.employee_name || '-'}</TableCell>}
                    <TableCell className="font-mono text-xs">{rec.date}</TableCell>
                    <TableCell className="font-mono text-xs">{formatTime(rec.check_in)}</TableCell>
                    <TableCell className="font-mono text-xs">{formatTime(rec.check_out)}</TableCell>
                    <TableCell className="font-mono text-xs">{rec.hours_worked || '-'}</TableCell>
                    <TableCell>
                      <Badge className={rec.status === 'present' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}>
                        {rec.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
